import React, { Fragment, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faEdit, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/api';
import { Dialog, DialogBackdrop, Transition } from '@headlessui/react';
import LoadingLogo from '../../components/Loading';
import Pagination from '../../components/Pagination';



const PetBreed = () => {
  const [data, setData] = useState([]);
  const [petTypes, setPetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1
  });

  // Fetch pet breeds on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [filter, currentPage]);

  // Fetch pet breeds from the backend with search, filter, and pagination
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pet-breeds`, {
        params: {
          search: searchTerm,
          pet_type: filter,
          page: currentPage,
        },
      });

      setData(response.data);
      setMeta(response.meta);
      setPetTypes(response.pet_type);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch pet breeds.');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pet breed?')) {
      try {
        await api.delete(`/pet-breeds/${id}`);
        toast.success('Pet breed deleted successfully.');
        fetchData(); // Refresh the table after deletion
      } catch (error) {
        toast.error('Failed to delete pet breed.');
      }
    }
  };

  const handleEdit = (item) => {
    setEditData(item);
    setIsModalOpen(true); // Show modal for editing
  };

  const handleAdd = () => {
    setEditData(null); // Reset any edit data
    setIsModalOpen(true); // Show modal for adding
  };

  const handleSave = () => {
    setIsModalOpen(false); // Hide modal after saving
    fetchData(); // Refresh list
  };

  const handleCancel = () => {
    setIsModalOpen(false); // Hide modal without saving
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  // Render individual page buttons
  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? 'bg-tblue text-white' : 'bg-white text-gray-700'
            } hover:bg-tblue hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };


  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto px-4 max-w-full overflow-x-hidden">
      {/* Header and Actions - Stack on mobile */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-4 md:space-y-0">
        <h1 className="text-tblue text-3xl font-bold">Pet Breeds</h1>
        <button
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent w-full md:w-auto justify-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New Pet Breed
        </button>
      </div>

      {/* Search and Filter - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mb-6">
        {/* Search Input */}
        <div className="flex-1 flex items-center border border-gray-300 rounded-lg p-2 shadow-md">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchData();
              }
            }}
            className="outline-none focus:ring-0 bg-transparent w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        {/* Filter Dropdown */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm w-full sm:w-auto"
        >
          <option value="all">All Pet Type</option>
          {petTypes.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      </div>

      {/* Table section with horizontal scroll */}
      <div className="overflow-x-auto relative  rounded-lg">
        <table className="min-w-full bg-white rounded-lg shadow-md">
          <thead className="bg-primary text-white">
            <tr>
              <th className="py-4 px-2 text-left sticky left-0 bg-primary z-2 border-r border-white">ID</th>
              <th className="py-4 px-2 text-left">Name</th>
              <th className="py-4 px-2 text-left">Type</th>
              <th className="py-4 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((pet) => (
                <tr key={pet.id} className="hover:bg-gray-50">
                  <td className="py-2 px-2 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-2 border-r border-gray-200">
                    {pet.id}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {pet.name}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {pet.type.name}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(pet)}
                        className="text-blue-500 hover:text-blue-700 p-2"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(pet.id)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-4 text-center text-gray-500">
                  No pet breeds available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Smart Pagination */}
      <Pagination
        meta={meta}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {/* CRUD Modal for Adding/Editing */}
      {isModalOpen && (
        <CRUModal
          item={editData}
          onSave={handleSave}
          onCancel={handleCancel}
          petTypes={petTypes}
        />
      )}
    </div>
  );
};

export default PetBreed;

export const CRUModal = ({ item, onSave, onCancel, petTypes }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '', // Track the selected pet type
  });

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        type: item.type.id // Preselect pet type for edit mode
      });
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, pet_type_id: formData.type }; // Map type_id for the API
      if (item) {
        // Update Pet Breed
        await api.put(`/pet-breeds/${item.id}`, payload);
        toast.success('Pet breed updated successfully');

      } else {
        // Create Pet Breed
        await api.post('/pet-breeds', payload);
        toast.success('Pet breed created successfully');
      }

      onSave(); // Callback to refresh the list and close the form
    } catch (error) {
      toast.error('Error saving pet breed. Please check the form.');
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {item ? 'Edit Pet Breed' : 'Add New Pet Breed'}
                </Dialog.Title>

                <div className="mt-4">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="block w-full p-2 border rounded-lg"
                      />
                    </div>

                    {/* Pet Type Dropdown */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pet Type</label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                        className="block w-full p-2 border rounded-lg"
                      >
                        <option value="">Select Pet Type</option>
                        {petTypes.map((petType) => (
                          <option key={petType.id} value={petType.id}>
                            {petType.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex justify-end mt-6">
                      <button
                        type="button"
                        onClick={onCancel}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-primary text-white px-4 py-2 rounded-lg"
                      >
                        {item ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
